import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { billingApi, billingQueryKeys } from "../../../shared/api/billing";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { formatDate, formatPhone } from "../../../shared/format";
import type { BillingPatientOption } from "../../../shared/types/billing";

interface Props {
  value: BillingPatientOption | null;
  onChange: (next: BillingPatientOption | null) => void;
  autoFocus?: boolean;
  error?: string;
}

export function InvoicePatientPicker({ value, onChange, autoFocus, error }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const debouncedQ = useDebounce(q, 200);
  const wrapRef = useRef<HTMLDivElement>(null);

  const results = useQuery({
    queryKey: billingQueryKeys.patientSearch(debouncedQ),
    queryFn: () => billingApi.searchPatients(debouncedQ),
    enabled: open,
  });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-input bg-muted/40 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground">{value.name}</div>
          <div className="truncate font-mono text-sm text-muted-foreground">
            {value.code ?? "—"}
            {value.phone && ` · ${formatPhone(value.phone)}`}
            {value.dateOfBirth && ` · ${formatDate(value.dateOfBirth)}`}
          </div>
        </div>
        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => onChange(null)}>
          Đổi
        </Button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="text"
        value={q}
        autoFocus={autoFocus}
        placeholder="Tìm bệnh nhân theo tên, SĐT, mã BN…"
        className="pl-9"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        aria-invalid={Boolean(error)}
      />
      {error && <div className="mt-1 text-sm text-destructive">{error}</div>}
      {open && (
        <div
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-input bg-card p-1 shadow-md"
          role="listbox"
        >
          {results.isLoading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Đang tìm…</div>
          )}
          {!results.isLoading && (results.data ?? []).length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Không tìm thấy bệnh nhân</div>
          )}
          {(results.data ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              role="option"
              className="flex w-full flex-col items-start gap-0.5 rounded-sm px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
              onClick={() => {
                onChange(p);
                setOpen(false);
                setQ("");
              }}
            >
              <div className="font-semibold text-foreground">{p.name}</div>
              <div className="font-mono text-sm text-muted-foreground">
                {p.code ?? "—"}
                {p.phone && ` · ${formatPhone(p.phone)}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
